import { A } from '@/components/atoms/A'
import { CardInnerTitle } from '@/components/atoms/CardInnerTitle'
import { LineClampText } from '@/components/atoms/LineClampText'
import { LoadingSpinner } from '@/components/atoms/LoadingSpinner'
import {
  Table,
  TableCell,
  TableHead,
  TableHeading,
  TableRow,
} from '@/components/atoms/Table'
import { TagButton, TagButtonGroup } from '@/components/atoms/TagButton'
import { IPOIcon } from '@/components/atoms/icons/IPOIcon'
import { EmptyCard } from '@/components/common/EmptyCard'
import { formatNumber } from '@/components/pages/companies/utils'
import {
  fetchDomesticBenchmarkCompanies,
  DomesticBenchmarksData,
} from '@/lib/api/company/domesticBenchmark'
import { companyDefaultImage } from '@/lib/company'
import { css } from '@emotion/react'
import Link from 'next/link'
import { Card, CompanyLogo, colors, font } from 'plume-ui'
import { useEffect, useState } from 'react'

type Props = {
  shortname: string
  className?: string
}

export const DomesticBenchmarkTable: React.FC<Props> = (props) => {
  const [domesticBenchmarkCompanies, setDomesticBenchmarkCompanies] = useState<
    DomesticBenchmarksData[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  useEffect(() => {
    setIsLoading(true)
    setDomesticBenchmarkCompanies([])
    fetchDomesticBenchmarkCompanies(props.shortname).then((res) => {
      setDomesticBenchmarkCompanies(res.domesticBenchmarkCompanies)
      setIsLoading(false)
    })
  }, [props.shortname])

  return (
    <Card data-test-id="domestic-benchmark" className={props.className}>
      <CardInnerTitle title="国内スタートアップ" />
      {isLoading ? (
        <LoadingSpinner size={3} />
      ) : domesticBenchmarkCompanies.length > 0 ? (
        <Table data-test-id="domestic-benchmark-companies">
          <TableHead dataTestId="domestic-benchmark-companies-header">
            <tr
              css={css`
                > th {
                  background-color: ${colors.white10} !important;
                }
              `}
            >
              <TableHeading>企業名</TableHeading>
              <TableHeading>設立年月日</TableHeading>
              <TableHeading align="center">
                <div>
                  <div>最新ラウンド</div>
                  <div>調達日</div>
                </div>
              </TableHeading>
              <TableHeading
                dataTestId="initial-series"
                align="center"
                help="スピーダが独自に定義する成長フェーズ"
                helpProps={{
                  linkTo: process.env.INITIAL_SERIES_HELP_ENDPOINT,

                  className: 'help-icon',
                }}
              >
                スピーダ
                <br />
                調達シリーズ
              </TableHeading>
              <TableHeading align="center" justify="end">
                <div>
                  <div>総調達額</div>
                  <OneThousandYen />
                </div>
              </TableHeading>
              <TableHeading align="center" justify="end">
                <div>
                  <div>調達後評価額</div>
                  <OneThousandYen />
                </div>
              </TableHeading>
            </tr>
          </TableHead>
          <tbody>
            {domesticBenchmarkCompanies.map((company, i) => (
              <TableRow highlightOnHover className="row" key={i}>
                <TableCell width="50%">
                  <div
                    css={css`
                      display: flex;
                    `}
                  >
                    <Link href={`/companies/${company.shortname}`} passHref>
                      <A dataTestId="company-logo">
                        <CompanyLogo
                          src={
                            company.imageUrl ||
                            companyDefaultImage(
                              company.isPublic ? 'IPO企業' : '未公開企業'
                            )
                          }
                          size="normal"
                          name={company.name}
                        />
                      </A>
                    </Link>

                    <div
                      css={css`
                        margin-left: 10px;
                        flex: 1;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                        `}
                      >
                        <div>
                          <Link
                            href={`/companies/${company.shortname}`}
                            passHref
                          >
                            <A
                              decoration="none"
                              css={css`
                                font-size: 15px;
                              `}
                              dataTestId="company-name"
                            >
                              {company.name}
                            </A>
                          </Link>
                        </div>
                        {company.isPublic && (
                          <IPOIcon
                            isHiddenTooltip={true}
                            className="ipo-label"
                            css={css`
                              margin-left: 3px;
                            `}
                          />
                        )}
                      </div>
                      <LineClampText
                        justify
                        lineNumber={5}
                        css={css`
                          font-size: 12px;
                          line-height: 1.5;
                        `}
                        stripLineBreaks
                        dataTestId="description"
                      >
                        {company.description}
                      </LineClampText>

                      <div
                        css={css`
                          margin-top: 6px;
                        `}
                      >
                        {company.tags && company.tags.length > 0 && (
                          <TagButtonGroup dataTestId="tags">
                            {company.tags.map((t) => (
                              <TagButton key={t.slug} tag={t} />
                            ))}
                          </TagButtonGroup>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  vertical="middle"
                  align="left"
                  dataTestId="established"
                >
                  {company.established}
                </TableCell>
                <TableCell vertical="middle" dataTestId="last-funding-on">
                  {company.lastFundraised}
                </TableCell>
                <TableCell vertical="middle" dataTestId="initial-series">
                  {company.isPublic ? '' : company.latestInitialSeries}
                </TableCell>
                <TableCell
                  vertical="middle"
                  align="right"
                  width="10%"
                  dataTestId="total-fundraise"
                >
                  {formatNumber(company.totalFunding)}
                </TableCell>
                <TableCell vertical="middle" align="right" width="10%">
                  {company.valuation && (
                    <>
                      <div data-test-id="valuation-value">
                        {formatNumber(company.valuation)}
                      </div>
                      {company.isPublic && (
                        <div
                          data-test-id="valuation-annotation"
                          css={css`
                            color: #999;
                            font-size: 11px;
                          `}
                        >
                          IPO前最終調達時
                        </div>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard dataTestId="domestic-benchmark-companies-empty">
          データ未作成です
        </EmptyCard>
      )}
    </Card>
  )
}

const OneThousandYen = () => (
  <div
    css={css`
      font-size: 11px;
      font-weight: ${font.weight.normal};
    `}
  >
    （千円）
  </div>
)
